import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { MessageCircle, CalendarCheck } from "lucide-react";
import { COMPANY } from "../content";
import { track } from "../lib/analytics";

// Per-route prefill text. Falls back to the generic message on routes
// that aren't explicitly mapped. Keys are exact pathnames; for parameterised
// routes (e.g. /portfolio/:slug), startsWith matching is applied below.
const ROUTE_TEXT = {
  "/": "Hi Time Furniture, I found you from the website and I'm interested in your services.",
  "/about": "Hi Time Furniture, I'd like to know more about your company. I found you from the website.",
  "/services":
    "Hi Time Furniture, I'm interested in renovation/fit-out work. I found you from the website.",
  "/portfolio":
    "Hi Time Furniture, I saw your portfolio and would like to discuss a similar project. I found you from the website.",
  "/ongoing-projects":
    "Hi Time Furniture, I saw your ongoing projects and would like to discuss a similar project. I found you from the website.",
  "/contact": "Hi Time Furniture, I'm contacting you from the website and would like assistance with a project.",
  "/holiday-homes":
    "Hi Time Furniture, I'm interested in holiday home furnishing/setup. I found you from the website.",
  "/curtains-dubai": "Hi Time Furniture, I'm interested in curtains/blinds. I found you from the website.",
  "/kitchen-renovation-dubai": "Hi Time Furniture, I'm interested in kitchen/wardrobe work. I found you from the website.",
  "/custom-wardrobes-dubai":
    "Hi Time Furniture, I'm interested in kitchen/wardrobe work. I found you from the website.",
  "/custom-joinery-dubai":
    "Hi Time Furniture, I'm interested in carpentry/joinery work. I found you from the website.",
  "/custom-sofas-dubai":
    "Hi Time Furniture, I'm interested in sofa upholstery/custom sofa. I found you from the website.",
  "/villa-renovation-dubai":
    "Hi Time Furniture, I'm interested in renovation/fit-out work. I found you from the website.",
  "/office-fit-out-dubai":
    "Hi Time Furniture, I'm interested in renovation/fit-out work. I found you from the website.",
  "/turnkey-contractor-uae":
    "Hi Time Furniture, I'm interested in renovation/fit-out work. I found you from the website.",
  "/interior-fit-out-dubai":
    "Hi Time Furniture, I'm interested in renovation/fit-out work. I found you from the website.",
  "/interior-fit-out-sharjah":
    "Hi Time Furniture, I'm interested in renovation/fit-out work. I found you from the website.",
  "/interior-fit-out-abu-dhabi":
    "Hi Time Furniture, I'm interested in renovation/fit-out work. I found you from the website.",
  "/interior-fit-out-company-dubai":
    "Hi Time Furniture, I'm interested in renovation/fit-out work. I found you from the website.",
  "/curtains-dubai-company":
    "Hi Time Furniture, I'm interested in curtains/blinds. I found you from the website.",
  "/products":
    "Hi Time Furniture, I'm interested in your furniture products. I found you from the website.",
};

// Routes where the floating controls should not appear at all.
const HIDE_ON = ["/admin/login", "/admin/dashboard", "/email-signature", "/thank-you"];

const DEFAULT_TEXT = "Hi Time Furniture, I'm contacting you from the website and would like assistance with a project.";

function pickText(pathname) {
  if (ROUTE_TEXT[pathname]) return ROUTE_TEXT[pathname];
  const p = pathname.toLowerCase();
  if (p.includes("flooring"))
    return "Hi Time Furniture, I'm interested in flooring work. I found you from the website.";
  if (p.includes("wallpaper") || p.includes("wall-panel"))
    return "Hi Time Furniture, I'm interested in wallpaper/wall panels. I found you from the website.";
  if (p.includes("curtain") || p.includes("blind"))
    return "Hi Time Furniture, I'm interested in curtains/blinds. I found you from the website.";
  if (p.includes("sofa") || p.includes("upholster"))
    return "Hi Time Furniture, I'm interested in sofa upholstery/custom sofa. I found you from the website.";
  if (p.includes("joinery") || p.includes("carpentry"))
    return "Hi Time Furniture, I'm interested in carpentry/joinery work. I found you from the website.";
  if (p.includes("kitchen") || p.includes("wardrobe"))
    return "Hi Time Furniture, I'm interested in kitchen/wardrobe work. I found you from the website.";
  if (pathname.startsWith("/ongoing-projects/"))
    return "Hi Time Furniture, I saw one of your ongoing projects and would like to discuss a similar project. I found you from the website.";
  if (pathname.startsWith("/portfolio/"))
    return "Hi Time Furniture, I saw your portfolio and would like to discuss a similar project. I found you from the website.";
  if (pathname.startsWith("/blog/"))
    return "Hi Time Furniture, I read your article and would like to discuss a project. I found you from the website.";
  if (pathname.startsWith("/services/"))
    return "Hi Time Furniture, I'm interested in renovation/fit-out work. I found you from the website.";
  return DEFAULT_TEXT;
}

export default function WhatsAppButton() {
  const { pathname } = useLocation();
  // While any form field is focused, hide the controls so they never overlap
  // inputs / the submit button (critical at 390px).
  const [fieldFocused, setFieldFocused] = useState(false);
  // Hide the desktop floating pill while any public form is in the viewport
  // so it never covers submit buttons (e.g. 1270×714 laptop screens).
  const [formInView, setFormInView] = useState(false);
  // On the homepage, the sticky mobile bar stays hidden until the hero CTA
  // area has scrolled out of view (so it never competes with the hero CTAs).
  const isHome = pathname === "/";
  const isContact = pathname === "/contact";
  const [heroVisible, setHeroVisible] = useState(isHome);
  const [reducedMotion, setReducedMotion] = useState(false);
  // Hide the sticky mobile strip while the curtain quote form is on screen so
  // it never overlaps the form's inputs; restored once scrolled past the form.
  const [quoteInView, setQuoteInView] = useState(false);

  useEffect(() => {
    const isField = (el) =>
      el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || "") || el.isContentEditable);
    const onFocus = (e) => { if (isField(e.target)) setFieldFocused(true); };
    const onBlur = (e) => { if (isField(e.target)) setFieldFocused(false); };
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", onBlur);
    try {
      setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch { /* ignore */ }
    return () => {
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", onBlur);
    };
  }, []);

  // Reveal-on-scroll only matters on the homepage.
  useEffect(() => {
    // Observe every public <form> on the current page; hide the desktop float
    // while any of them is on screen.
    if (!("IntersectionObserver" in window)) return undefined;
    const visible = new Set();
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) visible.add(en.target); else visible.delete(en.target);
      });
      setFormInView(visible.size > 0);
    }, { threshold: 0.05 });
    const attach = () => document.querySelectorAll("form").forEach((f) => obs.observe(f));
    const t = setTimeout(attach, 400);
    return () => { clearTimeout(t); obs.disconnect(); setFormInView(false); };
  }, [pathname]);

  useEffect(() => {
    setQuoteInView(false);
    if (!("IntersectionObserver" in window)) return undefined;
    let obs;
    const t = setTimeout(() => {
      const el = document.getElementById("curtain-quote");
      if (!el) return;
      obs = new IntersectionObserver(
        ([en]) => setQuoteInView(en.isIntersecting),
        { threshold: 0.05 },
      );
      obs.observe(el);
    }, 400);
    return () => { clearTimeout(t); if (obs) obs.disconnect(); };
  }, [pathname]);

  useEffect(() => {
    if (pathname !== "/") { setHeroVisible(false); return undefined; }
    setHeroVisible(true);
    let observer;
    let cleanupScroll;
    const el = document.querySelector('[data-testid="hero-cta-consultation"]');
    if (el && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        ([entry]) => setHeroVisible(entry.isIntersecting),
        { threshold: 0 },
      );
      observer.observe(el);
    } else {
      const onScroll = () => setHeroVisible(window.scrollY < window.innerHeight * 0.6);
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
      cleanupScroll = () => window.removeEventListener("scroll", onScroll);
    }
    return () => {
      if (observer) observer.disconnect();
      if (cleanupScroll) cleanupScroll();
    };
  }, [pathname]);

  if (HIDE_ON.some((r) => pathname.startsWith(r))) return null;

  const text = encodeURIComponent(pickText(pathname));
  const link = `https://wa.me/${COMPANY.whatsapp}?text=${text}`;

  // Sticky mobile bar rules:
  //  - never on /contact (user is already in the booking flow)
  //  - never on /custom-wardrobes-dubai (that page renders its own wardrobe bar)
  //  - never while a form field is focused
  //  - on the homepage, only once the hero CTA area has scrolled away
  const isWardrobe = pathname === "/custom-wardrobes-dubai";
  const isCurtains = pathname.startsWith("/curtains-dubai");
  const isHoliday = pathname.startsWith("/holiday-homes");
  const showMobileBar = !isContact && !isWardrobe && !fieldFocused && !quoteInView && !(isHome && heroVisible);
  const barMotion = reducedMotion ? "" : "transition-transform duration-200";

  return (
    <>
      {/* Desktop / tablet: compact floating WhatsApp pill (~48px tall). */}
      <a
        href={link}
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-float-button"
        aria-label="Chat on WhatsApp"
        className={`hidden sm:inline-flex fixed z-50 bottom-6 right-6 group items-center gap-3 bg-[#6E1F2C] text-white pl-3 pr-5 h-12 shadow-[0_20px_40px_-10px_rgba(110,31,44,0.55)] hover:bg-[#56151F] transition-all ${
          fieldFocused || formInView ? "opacity-0 pointer-events-none translate-y-4" : "opacity-100"
        }`}
      >
        <span className="relative flex h-8 w-8 items-center justify-center bg-[#EDE1C8] text-[#6E1F2C]">
          <MessageCircle size={17} />
        </span>
        <span className="text-[11px] tracking-[0.28em] uppercase">Chat on WhatsApp</span>
      </a>

      {/* Mobile: compact sticky bottom action bar. */}
      {!isContact && !isWardrobe && (
        <div
          data-testid="mobile-action-bar"
          aria-hidden={!showMobileBar}
          className={`sm:hidden fixed z-50 inset-x-0 bottom-0 grid grid-cols-2 border-t border-[#6E1F2C]/20 bg-[#FAF5EC]/95 backdrop-blur ${barMotion} ${
            showMobileBar ? "translate-y-0" : "translate-y-full pointer-events-none"
          }`}
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            data-testid="mobile-bar-whatsapp"
            tabIndex={showMobileBar ? 0 : -1}
            className="flex items-center justify-center gap-2 h-14 text-[#6E1F2C] text-[11px] tracking-[0.22em] uppercase border-r border-[#6E1F2C]/20"
          >
            <MessageCircle size={16} /> WhatsApp
          </a>
          <Link
            to={isCurtains ? "/curtains-dubai#curtain-quote" : isHoliday ? "/holiday-homes#holiday-inquiry" : "/contact"}
            data-testid="mobile-bar-cta"
            tabIndex={showMobileBar ? 0 : -1}
            onClick={() => track.siteVisitCta("mobile_bar")}
            className="flex items-center justify-center gap-2 h-14 bg-[#6E1F2C] text-[#FAF5EC] text-[11px] tracking-[0.22em] uppercase"
          >
            <CalendarCheck size={16} /> {isCurtains ? "Book a Measurement Visit" : isHoliday ? "Get My Furnishing Quote" : "Book Site Visit"}
          </Link>
        </div>
      )}
    </>
  );
}
